<template>
  <f7-page name="admin-dashboard" class="bg-surface font-body-md text-on-surface" @page:init="onPageInit">
    <f7-navbar no-shadow  back-link>
      <f7-nav-title >
        {{ $t('adminDashboard.title') }}
      </f7-nav-title>
      <f7-nav-right>
        <f7-link
          icon-f7="arrow_clockwise"
          @click="refreshAll"
          :class="{ rotating: adminLoading || marketplaceLoading }"
        />
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <!-- Supervisor Context Banner -->
        <div class="px-gutter-mobile pt-3 pb-1">
          <div class="bg-surface-container-low rounded-xl p-3.5 shadow-sm border border-primary-container/10 display-flex align-items-center justify-content-space-between gap-3">
            <div class="display-flex align-items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-primary-container/10 display-flex align-items-center justify-content-center text-primary">
                <f7-icon material="admin_panel_settings" :size="22" />
              </div>
              <div>
                <p class="font-headline-sm text-headline-sm text-on-surface font-bold tracking-tight">Superviseur Réseau</p>
                <div class="display-flex align-items-center gap-1.5">
                  <span class="w-2 h-2 rounded-full bg-emerald-600 animate-pulse"></span>
                  <span class="font-label-sm text-label-sm text-outline">Nœud UEMOA - Côte d'Ivoire · Actif</span>
                </div>
              </div>
            </div>
            <span class="font-label-sm text-label-sm bg-secondary-container/40 text-on-secondary-container px-2.5 py-1 rounded-full font-bold display-flex align-items-center gap-1 flex-shrink-0">
              <f7-icon material="verified" :size="14" /> Audit N2
            </span>
          </div>
        </div>

        <!-- Tab switcher -->
        <f7-segmented strong round class="dash-tabs my-2">
          <f7-button round raised
            :active="activeTab === 'admin'"
            @click="switchTab('admin')"
          >{{ $t('adminDashboard.title') }}</f7-button>
          <f7-button round raised
            :active="activeTab === 'marketplace'"
            @click="switchTab('marketplace')"
          >{{ $t('marketplaceDashboard.title') }}</f7-button>
          <f7-button round raised
            v-if="routeGroupId"
            :active="activeTab === 'group'"
            @click="switchTab('group')"
          >{{ $t('groupDashboard.title') }}</f7-button>
        </f7-segmented>

        <!-- Error banner -->
        <f7-block v-if="loadError" class="dash-error">
          <f7-icon f7="exclamationmark_triangle_fill" />
          <span>{{ loadError }}</span>
          <f7-button small fill @click="loadCurrent">{{ $t('common.retry') }}</f7-button>
        </f7-block>

        <!-- ====== ADMIN TAB ====== -->
        <template v-if="activeTab === 'admin'">
          <f7-block v-if="adminLoading && !adminData" class="text-align-center" style="padding:32px 0">
            <f7-preloader />
            <p class="margin-top">{{ $t('adminDashboard.loading') }}</p>
          </f7-block>
          <div v-else-if="adminData">
            <AdminMetricGrid :data="adminData" />

            <AdminKycSection :data="adminData" />
            <AdminAdSection :data="adminData" />
            <AdminGrowthSection :data="adminData" />
            <AdminMpSection :data="adminData" />
            <AdminGbSection :data="adminData" />
            <AdminInvitesSection :data="adminData" />
            <AdminPaymentLinksSection :data="adminData" />
            <AdminDocSection :data="adminData" />
            <AdminAvocashSection />

            <f7-block strong inset class="text-align-center">
              <small class="text-color-gray">{{ lastAdminUpdate }}</small>
            </f7-block>
          </div>
        </template>

        <!-- ====== MARKETPLACE TAB ====== -->
        <template v-if="activeTab === 'marketplace'">
          <f7-block v-if="marketplaceLoading && !marketplaceData" class="text-align-center" style="padding:32px 0">
            <f7-preloader />
            <p class="margin-top">{{ $t('marketplaceDashboard.loading') }}</p>
          </f7-block>
          <div v-else-if="marketplaceData">
            <MarketplaceMetricGrid :data="marketplaceData" />
            <MarketplaceOrdersSection :data="marketplaceData" />
            <MarketplaceGbSection :data="marketplaceData" />
            <MarketplaceStbSection :data="marketplaceData" />

            <f7-block strong inset class="text-align-center">
              <small class="text-color-gray">{{ lastMarketplaceUpdate }}</small>
            </f7-block>
          </div>
        </template>

        <!-- ====== GROUP TAB ====== -->
        <template v-if="activeTab === 'group' && routeGroupId">
          <f7-block v-if="groupLoading && !groupData" class="text-align-center" style="padding:32px 0">
            <f7-preloader />
            <p class="margin-top">{{ $t('groupDashboard.loading') }}</p>
          </f7-block>
          <div v-else-if="groupData">
            <GroupMetricGrid :data="groupData" />
            <GroupCampaignsSection :data="groupData" />
            <GroupPayoutsSection :data="groupData" />
            <GroupMpSection :data="groupData" />
            <GroupEventsSection :data="groupData" />
            <GroupAccessSection :data="groupData" />

            <f7-block strong inset class="text-align-center">
              <small class="text-color-gray">{{ lastGroupUpdate }}</small>
            </f7-block>
          </div>
        </template>

      </template>
    </AppLayout>
  </f7-page>
</template>

<style scoped>
.dash-tabs {
  margin: 8px 16px 0;
  display: flex;
  gap: 4px;
}
.dash-tabs .button {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  min-width: 0;
}
.dash-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--f7-theme-color);
  font-size: 13px;
}
.dash-error .button { flex-shrink: 0; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.rotating { animation: spin 1s linear infinite; }
</style>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AppLayout from '../components/common/AppLayout.vue';

import {
  useDashboardMetrics,
} from '../js/composables/useDashboardMetrics.js';
import AdminMetricGrid from '../components/dashboard/AdminMetricGrid.vue';
import AdminKycSection from '../components/dashboard/AdminKycSection.vue';
import AdminAdSection from '../components/dashboard/AdminAdSection.vue';
import AdminGrowthSection from '../components/dashboard/AdminGrowthSection.vue';
import AdminMpSection from '../components/dashboard/AdminMpSection.vue';
import AdminGbSection from '../components/dashboard/AdminGbSection.vue';
import AdminInvitesSection from '../components/dashboard/AdminInvitesSection.vue';
import AdminPaymentLinksSection from '../components/dashboard/AdminPaymentLinksSection.vue';
import AdminDocSection from '../components/dashboard/AdminDocSection.vue';
import AdminAvocashSection from '../components/dashboard/AdminAvocashSection.vue';
import MarketplaceMetricGrid from '../components/dashboard/MarketplaceMetricGrid.vue';
import MarketplaceOrdersSection from '../components/dashboard/MarketplaceOrdersSection.vue';
import MarketplaceGbSection from '../components/dashboard/MarketplaceGbSection.vue';
import MarketplaceStbSection from '../components/dashboard/MarketplaceStbSection.vue';
import GroupMetricGrid from '../components/dashboard/GroupMetricGrid.vue';
import GroupCampaignsSection from '../components/dashboard/GroupCampaignsSection.vue';
import GroupPayoutsSection from '../components/dashboard/GroupPayoutsSection.vue';
import GroupMpSection from '../components/dashboard/GroupMpSection.vue';
import GroupEventsSection from '../components/dashboard/GroupEventsSection.vue';
import GroupAccessSection from '../components/dashboard/GroupAccessSection.vue';

import i18n from '../js/i18n.js';
const { t } = i18n.global;

const {
  adminMetrics: adminData,
  marketplaceMetrics: marketplaceData,
  groupMetrics: groupData,
  adminLoading,
  marketplaceLoading,
  groupLoading,
  adminError,
  marketplaceError,
  groupError,
  fetchAdminCurrent,
  fetchMarketplaceCurrent,
  fetchMarketplaceByType,
  fetchGroupCurrent,
  switchTab: _switchTab,
  clearAdmin,
  clearMarketplace,
  clearGroup,
} = useDashboardMetrics();

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const routeGroupId = computed(() => props.f7route?.params?.groupId || '');
const activeTab = ref('admin');
const loadError = ref(null);

function switchTab(tab) {
  activeTab.value = tab;
  loadError.value = null;
  if (tab === 'admin') fetchAdminCurrent().catch(() => {});
  else if (tab === 'marketplace') fetchMarketplaceCurrent().catch(() => {});
  else if (tab === 'group' && routeGroupId.value) fetchGroupCurrent(routeGroupId.value).catch(() => {});
}

async function loadCurrent() {
  loadError.value = null;
  try {
    if (activeTab.value === 'admin') await fetchAdminCurrent();
    else if (activeTab.value === 'marketplace') await fetchMarketplaceCurrent();
    else if (activeTab.value === 'group') await fetchGroupCurrent(routeGroupId.value);
  } catch (err) {
    loadError.value = err?.response?.data?.message || err?.message || t('adminDashboard.noData');
  }
}

async function refreshAll() {
  loadError.value = null;
  try {
    if (activeTab.value === 'admin') await fetchAdminCurrent();
    else if (activeTab.value === 'marketplace') await fetchMarketplaceCurrent();
    else if (activeTab.value === 'group') await fetchGroupCurrent(routeGroupId.value);
  } catch (err) {
    loadError.value = err?.response?.data?.message || err?.message || t('adminDashboard.noData');
  }
}

function onPageInit() {
  const path = props.f7route?.path || '';
  if (path.includes('marketplace')) activeTab.value = 'marketplace';
  else if (routeGroupId.value) activeTab.value = 'group';
  else activeTab.value = 'admin';
  loadCurrent();
}

onMounted(() => {
  loadCurrent();
});
</script>